import { __awaiter } from "tslib";
import { computed, makeAutoObservable, observable, reaction, runInAction } from "mobx";
import { logCurrentHistoryEvent } from "../../models/history/log-history-event";
// How long each stop stays on screen while the history plays itself.
export const kPlaybackStepMs = 500;
const kPlaybackFailureWarning = "History playback could not apply some changes and was stopped.";
/**
 * The state behind the playback control: where the reader is in a document's history, what
 * the slider offers them, and the auto-play that walks them through it.
 *
 * Comments are not read here. They arrive from Firestore through React Query hooks, so the
 * component pushes them in with `setComments`.
 */
export class PlaybackControlModel {
    /**
     * @param initialRequest what the reader has already asked for. `"end"` for a control
     * opened normally; `undefined` when a link is about to send the document to a particular
     * history entry, so that the thumb follows it there rather than holding it at the end.
     */
    constructor(treeManager, initialRequest) {
        Object.defineProperty(this, "comments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "simplePathComments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        /**
         * What the reader last asked to see.
         *
         * `"end"` is a request to be wherever the end of the slider is, rather than for the stop
         * that happens to be last right now — that is what following along means, and it is how a
         * control that has not been touched starts out. A number is a request for that one stop:
         * several stops can share a history position — a comment and the entry before it — so once
         * the document reaches it, this is the only record of which of them was meant.
         * `undefined` means no request stands, and the thumb simply follows the document. That is
         * how a control opens when a link is about to send the document to a particular entry:
         * the reader asked for that entry, not for the end.
         */
        Object.defineProperty(this, "requestedStop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sliderPlaying", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "playbackFailureWarning", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "selectedFailure", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "treeManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "advanceTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        // The stop this model is on its way to: queued for the seek loop, or being sought now.
        // rc-slider reports every mouse move of a drag, and only the last of them is worth
        // seeking to.
        Object.defineProperty(this, "queuedStopIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "seekLoop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: undefined
        });
        Object.defineProperty(this, "followEndDisposer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.treeManager = treeManager;
        this.requestedStop = initialRequest;
        makeAutoObservable(this, {
            // The comment queries hand back a new array whenever their results change, so tracking
            // the reference is enough, and it leaves the comment objects themselves alone.
            comments: observable.ref,
            simplePathComments: observable.ref,
            selectedFailure: observable.ref,
            // A TreeManager's volatile state is shallow, so pushes onto historyPlaybackFailures are
            // not observable and could not invalidate a cached computed.
            uniqueFailures: false,
            treeManager: false,
            advanceTimer: false,
            seekLoop: false,
            followEndDisposer: false,
            requestedStopIndex: computed,
            queuedStopIndex: observable
        }, { autoBind: true });
        // A reader following along asked for the end of the slider, so a new stop appearing past
        // them is a new answer to that request. Entries and comments arrive the same way here.
        this.followEndDisposer = reaction(() => this.lastStopIndex, lastStopIndex => {
            if (this.requestedStop === "end")
                void this.goToSliderStop(lastStopIndex);
        });
    }
    // The stop a request names, which for a request to follow along is wherever the end is now.
    get requestedStopIndex() {
        return this.requestedStop === "end" ? this.lastStopIndex : this.requestedStop;
    }
    setComments(comments, simplePathComments) {
        this.comments = comments !== null && comments !== void 0 ? comments : [];
        this.simplePathComments = simplePathComments !== null && simplePathComments !== void 0 ? simplePathComments : [];
    }
    get allComments() {
        return [...this.comments, ...this.simplePathComments]
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    }
    get sliderStops() {
        // History entries must stay in index order (their position in the array),
        // not sorted by created time. Created times can be out of order when
        // sub-actions complete before their parent action.
        const historyStops = this.treeManager.document.history.map((entry, index) => ({ kind: "history", entry, created: entry.created, index }));
        // Insert comments at the correct position among history entries based
        // on the comment's created time. Each comment goes after the last
        // history entry whose created time is <= the comment's created time.
        // allComments is already in created order.
        const allComments = this.allComments;
        const stops = [{ kind: "initial" }];
        let commentIdx = 0;
        for (const historyStop of historyStops) {
            while (commentIdx < allComments.length &&
                allComments[commentIdx].createdAt.getTime() < historyStop.created.getTime()) {
                const comment = allComments[commentIdx];
                stops.push({ kind: "comment", entry: comment, created: comment.createdAt });
                commentIdx++;
            }
            stops.push(historyStop);
        }
        while (commentIdx < allComments.length) {
            const comment = allComments[commentIdx];
            stops.push({ kind: "comment", entry: comment, created: comment.createdAt });
            commentIdx++;
        }
        return stops;
    }
    get lastStopIndex() {
        return this.sliderStops.length - 1;
    }
    /**
     * The stop the document is at.
     *
     * This follows the document rather than what was asked of it. Its history position can move
     * without the slider being touched — a link into a document's history seeks with
     * goToHistoryEntryPosition — and a seek can be left short of where it was asked to go by an
     * entry that fails to apply. While a seek is running this still reports where the document
     * has got to, so it is sliderValue, not this, that says where the thumb belongs.
     */
    get currentStopIndex() {
        const requested = this.requestedStopIndex;
        const historyPosition = this.treeManager.numHistoryEventsApplied;
        // The position is unknown only while the manager looks up the document's last entry, which
        // can still be running when the control mounts. There is nothing to follow yet.
        if (historyPosition === undefined)
            return requested !== null && requested !== void 0 ? requested : this.lastStopIndex;
        // Several stops can share one history position — a comment and the entry before it — so
        // a stop that already represents the position is left alone rather than snapped onto the
        // history entry.
        if (requested !== undefined &&
            this.historyPositionForStopIndex(requested) === historyPosition) {
            return requested;
        }
        return this.stopIndexForHistoryPosition(historyPosition);
    }
    /**
     * The value to show on the slider.
     *
     * While a seek of our own is running the document has not moved yet, so currentStopIndex
     * still reports where the reader came from. Showing that would snap the thumb out from
     * under their cursor and let it crawl after them, so the destination stands in until the
     * move settles.
     *
     * Anything else that moves the document goes through currentStopIndex, which checks
     * requestedStopIndex still describes where the document is before answering with it.
     * Reading requestedStopIndex here instead would skip that check and put the thumb back on
     * a stop the reader asked for before the document was moved out from under it.
     */
    get sliderValue() {
        var _a;
        return (_a = this.queuedStopIndex) !== null && _a !== void 0 ? _a : this.currentStopIndex;
    }
    // The stop the thumb is on. The readouts label the thumb, so they follow it to where a
    // running seek is headed rather than staying on the stop the document has yet to leave.
    get sliderStop() {
        return this.sliderStops[this.sliderValue];
    }
    // When the change or comment the thumb is on was made. Undefined at the initial stop,
    // which describes no change.
    get sliderStopTime() {
        const stop = this.sliderStop;
        return (stop === null || stop === void 0 ? void 0 : stop.kind) === "initial" ? undefined : stop === null || stop === void 0 ? void 0 : stop.created;
    }
    // The entry the thumb's stop applied, if it is a history stop rather than a comment.
    get sliderStopHistoryIndex() {
        const stop = this.sliderStop;
        return (stop === null || stop === void 0 ? void 0 : stop.kind) === "history" ? stop.index : undefined;
    }
    get playbackDisabled() {
        return this.treeManager.numHistoryEventsApplied === undefined ||
            this.currentStopIndex === this.lastStopIndex;
    }
    /**
     * A slider stop is a state of the document rather than a change to it. A history position
     * counts applied entries, so position p is the document with entries 0..p-1 applied.
     *
     * A history stop is the document once its entry has been applied. A comment stop takes the
     * position of the entry before it, which is the document its author was looking at. The
     * initial stop is the document before any of the history was applied.
     */
    historyPositionForStopIndex(stopIndex) {
        const stops = this.sliderStops;
        for (let i = stopIndex; i >= 0; i--) {
            const stop = stops[i];
            if ((stop === null || stop === void 0 ? void 0 : stop.kind) === "history")
                return stop.index + 1;
        }
        return 0;
    }
    // The inverse: the stop that represents a history position. Every position within the
    // history has a history stop of its own, including the last; a comment written after the
    // final entry stands beyond that stop rather than in place of it.
    stopIndexForHistoryPosition(historyPosition) {
        if (historyPosition <= 0)
            return 0;
        // Nothing should ask for a position past the end of the history, but the last stop is
        // the closest thing to one if anything does.
        if (historyPosition > this.treeManager.document.history.length)
            return this.lastStopIndex;
        return this.sliderStops.findIndex(s => s.kind === "history" && s.index === historyPosition - 1);
    }
    getCommentLocation(comment) {
        const index = this.sliderStops.findIndex(s => s.kind === "comment" && s.entry.id === comment.id);
        return this.railLocation(index);
    }
    // One marker per failing entry, however many times playback has run into it.
    get uniqueFailures() {
        if (this.lastStopIndex <= 0)
            return [];
        const seenIndices = new Set();
        const markers = [];
        for (const failure of this.treeManager.historyPlaybackFailures) {
            if (seenIndices.has(failure.historyIndex))
                continue;
            seenIndices.add(failure.historyIndex);
            // The stop that would have applied the failing entry, which is the one playback
            // could not reach.
            markers.push({
                failure,
                location: this.railLocation(this.stopIndexForHistoryPosition(failure.historyIndex + 1))
            });
        }
        return markers;
    }
    setSelectedFailure(failure) {
        this.selectedFailure = failure;
    }
    goToSliderStop(stopIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            // Asking for the last stop is asking to be at the end. The behavior we want when they
            // are at the end is to stay at the end as new stops are added, so record the request as
            // "end" rather than as the index that happens to be last right now.
            this.requestedStop = stopIndex >= this.lastStopIndex ? "end" : stopIndex;
            this.queuedStopIndex = stopIndex;
            // Only one seek runs at a time. A request arriving while one is running waits for it,
            // and the loop then goes to wherever the reader has got to by that point rather than
            // replaying every stop they dragged across.
            if (!this.seekLoop) {
                this.seekLoop = this.runSeekLoop().finally(() => { this.seekLoop = undefined; });
            }
            yield this.seekLoop;
        });
    }
    goToComment(comment) {
        return __awaiter(this, void 0, void 0, function* () {
            const index = this.sliderStops.findIndex(s => s.kind === "comment" && s.entry.id === comment.id);
            if (index !== -1) {
                yield this.goToSliderStop(index);
            }
        });
    }
    togglePlay(playing) {
        const playStatus = playing !== undefined ? playing : !this.sliderPlaying;
        logCurrentHistoryEvent(this.treeManager, playStatus ? "playStart" : "playStop");
        this.sliderPlaying = playStatus;
        this.clearAdvanceTimer();
        if (playStatus)
            this.scheduleAdvance();
    }
    dispose() {
        // A step whose seek is still running has no timer left to clear, so this is what stops it.
        // Set directly rather than through togglePlay, which would log a pause nobody performed.
        this.sliderPlaying = false;
        this.clearAdvanceTimer();
        this.followEndDisposer();
    }
    runSeekLoop() {
        return __awaiter(this, void 0, void 0, function* () {
            // A stop stays queued for as long as its own seek is running, not just while it waits,
            // so that sliderValue holds the destination from the first request until the document
            // has settled there.
            while (this.queuedStopIndex !== undefined) {
                const target = this.queuedStopIndex;
                const position = this.historyPositionForStopIndex(target);
                // A throw leaves the document part-way, like a seek that stops short, so it is reported
                // the same way. Catching it here is also what lets the queue below clear, bringing the
                // thumb back to the document rather than leaving it on a stop nothing reached.
                let threw = false;
                try {
                    yield this.treeManager.goToHistoryEntryPosition(position);
                }
                catch (error) {
                    console.warn("PlaybackControlModel: seek to history position", position, "failed:", error);
                    threw = true;
                }
                runInAction(() => {
                    // A failed entry can block the move, leaving the document short of where it was
                    // asked to go. currentStopIndex reports where it landed; the warning says why that
                    // is not where the reader clicked.
                    const actual = this.treeManager.numHistoryEventsApplied;
                    const blocked = threw || (actual !== undefined && actual !== position);
                    this.playbackFailureWarning = blocked ? kPlaybackFailureWarning : null;
                    // The stop that was asked for was never reached, so there is nothing left to prefer
                    // over wherever the document actually stopped.
                    if (blocked)
                        this.requestedStop = undefined;
                    // A newer request may have arrived while this seek ran. Leave its target for the
                    // next turn of the loop.
                    if (this.queuedStopIndex === target)
                        this.queuedStopIndex = undefined;
                });
            }
        });
    }
    // Percentage along the rail, which runs from the initial stop at 0 to the last stop.
    railLocation(stopIndex) {
        if (this.lastStopIndex <= 0)
            return 0;
        return Math.max(0, Math.min(100, 100 * (stopIndex / this.lastStopIndex)));
    }
    scheduleAdvance() {
        this.advanceTimer = setTimeout(() => void this.advance(), kPlaybackStepMs);
    }
    clearAdvanceTimer() {
        if (this.advanceTimer !== undefined) {
            clearTimeout(this.advanceTimer);
            this.advanceTimer = undefined;
        }
    }
    // Each step schedules the next once its seek has settled, so the pace does not depend on
    // how long the document takes to replay.
    advance() {
        return __awaiter(this, void 0, void 0, function* () {
            this.advanceTimer = undefined;
            if (!this.sliderPlaying)
                return;
            if (this.currentStopIndex >= this.lastStopIndex) {
                this.togglePlay(false);
                return;
            }
            yield this.goToSliderStop(this.currentStopIndex + 1);
            // Play may have been stopped, or the document disposed, while the seek was running.
            if (!this.sliderPlaying)
                return;
            // Stop at an entry that cannot be applied rather than retrying it every step.
            if (this.playbackFailureWarning) {
                this.togglePlay(false);
                return;
            }
            this.scheduleAdvance();
        });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoicGxheWJhY2stY29udHJvbC1tb2RlbC5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uLy4uL3NyYy9jb21wb25lbnRzL3BsYXliYWNrL3BsYXliYWNrLWNvbnRyb2wtbW9kZWwudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IjtBQUFBLE9BQU8sRUFDTCxRQUFRLEVBQXFCLGtCQUFrQixFQUFFLFVBQVUsRUFBRSxRQUFRLEVBQUUsV0FBVyxFQUNuRixNQUFNLE1BQU0sQ0FBQztBQUtkLE9BQU8sRUFBRSxzQkFBc0IsRUFBRSxNQUFNLHdDQUF3QyxDQUFDO0FBR2hGLHFFQUFxRTtBQUNyRSxNQUFNLENBQUMsTUFBTSxlQUFlLEdBQUcsR0FBRyxDQUFDO0FBRW5DLE1BQU0sdUJBQXVCLEdBQUcsZ0VBQWdFLENBQUM7QUE0QmpHOzs7Ozs7R0FNRztBQUNILE1BQU0sT0FBTyxvQkFBb0I7SUE2Qi9COzs7O09BSUc7SUFDSCxZQUFZLFdBQXlDLEVBQUUsY0FBdUM7UUFqQzlGOzs7O21CQUFzQyxFQUFFO1dBQUM7UUFDekM7Ozs7bUJBQWdELEVBQUU7V0FBQztRQUNuRDs7Ozs7Ozs7Ozs7V0FXRztRQUNIOzs7OztXQUF1QztRQUN2Qzs7OzttQkFBZ0IsS0FBSztXQUFDO1FBQ3RCOzs7O21CQUF3QyxJQUFJO1dBQUM7UUFDN0M7Ozs7bUJBQWlELElBQUk7V0FBQztRQUU5Qzs7Ozs7V0FBMEM7UUFDMUM7Ozs7bUJBQTBELFNBQVM7V0FBQztRQUM1RSx1RkFBdUY7UUFDdkYsbUZBQW1GO1FBQ25GLGNBQWM7UUFDTjs7OzttQkFBc0MsU0FBUztXQUFDO1FBQ2hEOzs7O21CQUFzQyxTQUFTO1dBQUM7UUFDaEQ7Ozs7O1dBQXFDO1FBUTNDLElBQUksQ0FBQyxXQUFXLEdBQUcsV0FBVyxDQUFDO1FBQy9CLElBQUksQ0FBQyxhQUFhLEdBQUcsY0FBYyxDQUFDO1FBQ3BDLGtCQUFrQixDQUU2QyxJQUFJLEVBQUU7WUFDbkUsdUZBQXVGO1lBQ3ZGLCtFQUErRTtZQUMvRSxRQUFRLEVBQUUsVUFBVSxDQUFDLEdBQUc7WUFDeEIsa0JBQWtCLEVBQUUsVUFBVSxDQUFDLEdBQUc7WUFDbEMsZUFBZSxFQUFFLFVBQVUsQ0FBQyxHQUFHO1lBQy9CLHdGQUF3RjtZQUN4Riw2REFBNkQ7WUFDN0QsY0FBYyxFQUFFLEtBQUs7WUFDckIsV0FBVyxFQUFFLEtBQUs7WUFDbEIsWUFBWSxFQUFFLEtBQUs7WUFDbkIsUUFBUSxFQUFFLEtBQUs7WUFDZixpQkFBaUIsRUFBRSxLQUFLO1lBQ3hCLGtCQUFrQixFQUFFLFFBQVE7WUFDNUIsZUFBZSxFQUFFLFVBQVU7U0FDNUIsRUFBRSxFQUFFLFFBQVEsRUFBRSxJQUFJLEVBQUUsQ0FBQyxDQUFDO1FBRXZCLHlGQUF5RjtRQUN6Rix1RkFBdUY7UUFDdkYsSUFBSSxDQUFDLGlCQUFpQixHQUFHLFFBQVEsQ0FDL0IsR0FBRyxFQUFFLENBQUMsSUFBSSxDQUFDLGFBQWEsRUFDeEIsYUFBYSxDQUFDLEVBQUU7WUFDZCxJQUFJLElBQUksQ0FBQyxhQUFhLEtBQUssS0FBSztnQkFBRSxLQUFLLElBQUksQ0FBQyxjQUFjLENBQUMsYUFBYSxDQUFDLENBQUM7UUFDNUUsQ0FBQyxDQUNGLENBQUM7SUFDSixDQUFDO0lBRUQsNEZBQTRGO0lBQzVGLElBQVksa0JBQWtCO1FBQzVCLE9BQU8sSUFBSSxDQUFDLGFBQWEsS0FBSyxLQUFLLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQyxhQUFhLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQyxhQUFhLENBQUM7SUFDaEYsQ0FBQztJQUVELFdBQVcsQ0FBQyxRQUFvQyxFQUFFLGtCQUE4QztRQUM5RixJQUFJLENBQUMsUUFBUSxHQUFHLFFBQVEsYUFBUixRQUFRLGNBQVIsUUFBUSxHQUFJLEVBQUUsQ0FBQztRQUMvQixJQUFJLENBQUMsa0JBQWtCLEdBQUcsa0JBQWtCLGFBQWxCLGtCQUFrQixjQUFsQixrQkFBa0IsR0FBSSxFQUFFLENBQUM7SUFDckQsQ0FBQztJQUVELElBQUksV0FBVztRQUNiLE9BQU8sQ0FBQyxHQUFHLElBQUksQ0FBQyxRQUFRLEVBQUUsR0FBRyxJQUFJLENBQUMsa0JBQWtCLENBQUM7YUFDbEQsSUFBSSxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUMsRUFBRSxFQUFFLENBQUMsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxPQUFPLEVBQUUsR0FBRyxDQUFDLENBQUMsU0FBUyxDQUFDLE9BQU8sRUFBRSxDQUFDLENBQUM7SUFDbkUsQ0FBQztJQUVELElBQUksV0FBVztRQUNiLDBFQUEwRTtRQUMxRSxxRUFBcUU7UUFDckUsbURBQW1EO1FBQ25ELE1BQU0sWUFBWSxHQUF5QixJQUFJLENBQUMsV0FBVyxDQUFDLFFBQVEsQ0FBQyxPQUFPLENBQUMsR0FBRyxDQUM5RSxDQUFDLEtBQUssRUFBRSxLQUFLLEVBQUUsRUFBRSxDQUFDLENBQUMsRUFBRSxJQUFJLEVBQUUsU0FBUyxFQUFFLEtBQUssRUFBRSxPQUFPLEVBQUUsS0FBSyxDQUFDLE9BQU8sRUFBRSxLQUFLLEVBQUUsQ0FBQyxDQUFDLENBQUM7UUFFakYsc0VBQXNFO1FBQ3RFLGtFQUFrRTtRQUNsRSxxRUFBcUU7UUFDckUsMkNBQTJDO1FBQzNDLE1BQU0sV0FBVyxHQUFHLElBQUksQ0FBQyxXQUFXLENBQUM7UUFDckMsTUFBTSxLQUFLLEdBQWtCLENBQUMsRUFBRSxJQUFJLEVBQUUsU0FBUyxFQUFFLENBQUMsQ0FBQztRQUNuRCxJQUFJLFVBQVUsR0FBRyxDQUFDLENBQUM7UUFDbkIsS0FBSyxNQUFNLFdBQVcsSUFBSSxZQUFZLEVBQUUsQ0FBQztZQUN2QyxPQUFPLFVBQVUsR0FBRyxXQUFXLENBQUMsTUFBTTtnQkFDL0IsV0FBVyxDQUFDLFVBQVUsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxPQUFPLEVBQUUsR0FBRyxXQUFXLENBQUMsT0FBTyxDQUFDLE9BQU8sRUFBRSxFQUFFLENBQUM7Z0JBQ25GLE1BQU0sT0FBTyxHQUFHLFdBQVcsQ0FBQyxVQUFVLENBQUMsQ0FBQztnQkFDeEMsS0FBSyxDQUFDLElBQUksQ0FBQyxFQUFFLElBQUksRUFBRSxTQUFTLEVBQUUsS0FBSyxFQUFFLE9BQU8sRUFBRSxPQUFPLEVBQUUsT0FBTyxDQUFDLFNBQVMsRUFBRSxDQUFDLENBQUM7Z0JBQzVFLFVBQVUsRUFBRSxDQUFDO1lBQ2YsQ0FBQztZQUNELEtBQUssQ0FBQyxJQUFJLENBQUMsV0FBVyxDQUFDLENBQUM7UUFDMUIsQ0FBQztRQUNELE9BQU8sVUFBVSxHQUFHLFdBQVcsQ0FBQyxNQUFNLEVBQUUsQ0FBQztZQUN2QyxNQUFNLE9BQU8sR0FBRyxXQUFXLENBQUMsVUFBVSxDQUFDLENBQUM7WUFDeEMsS0FBSyxDQUFDLElBQUksQ0FBQyxFQUFFLElBQUksRUFBRSxTQUFTLEVBQUUsS0FBSyxFQUFFLE9BQU8sRUFBRSxPQUFPLEVBQUUsT0FBTyxDQUFDLFNBQVMsRUFBRSxDQUFDLENBQUM7WUFDNUUsVUFBVSxFQUFFLENBQUM7UUFDZixDQUFDO1FBQ0QsT0FBTyxLQUFLLENBQUM7SUFDZixDQUFDO0lBRUQsSUFBSSxhQUFhO1FBQ2YsT0FBTyxJQUFJLENBQUMsV0FBVyxDQUFDLE1BQU0sR0FBRyxDQUFDLENBQUM7SUFDckMsQ0FBQztJQUVEOzs7Ozs7OztPQVFHO0lBQ0gsSUFBSSxnQkFBZ0I7UUFDbEIsTUFBTSxTQUFTLEdBQUcsSUFBSSxDQUFDLGtCQUFrQixDQUFDO1FBQzFDLE1BQU0sZUFBZSxHQUFHLElBQUksQ0FBQyxXQUFXLENBQUMsdUJBQXVCLENBQUM7UUFDakUsMkZBQTJGO1FBQzNGLGdGQUFnRjtRQUNoRixJQUFJLGVBQWUsS0FBSyxTQUFTO1lBQUUsT0FBTyxTQUFTLGFBQVQsU0FBUyxjQUFULFNBQVMsR0FBSSxJQUFJLENBQUMsYUFBYSxDQUFDO1FBQzFFLHdGQUF3RjtRQUN4Rix5RkFBeUY7UUFDekYsaUJBQWlCO1FBQ2pCLElBQUksU0FBUyxLQUFLLFNBQVM7WUFDdkIsSUFBSSxDQUFDLDJCQUEyQixDQUFDLFNBQVMsQ0FBQyxLQUFLLGVBQWUsRUFBRSxDQUFDO1lBQ3BFLE9BQU8sU0FBUyxDQUFDO1FBQ25CLENBQUM7UUFDRCxPQUFPLElBQUksQ0FBQywyQkFBMkIsQ0FBQyxlQUFlLENBQUMsQ0FBQztJQUMzRCxDQUFDO0lBRUQ7Ozs7Ozs7Ozs7OztPQVlHO0lBQ0gsSUFBSSxXQUFXOztRQUNiLE9BQU8sTUFBQSxJQUFJLENBQUMsZUFBZSxtQ0FBSSxJQUFJLENBQUMsZ0JBQWdCLENBQUM7SUFDdkQsQ0FBQztJQUVELHVGQUF1RjtJQUN2Rix3RkFBd0Y7SUFDeEYsSUFBSSxVQUFVO1FBQ1osT0FBTyxJQUFJLENBQUMsV0FBVyxDQUFDLElBQUksQ0FBQyxXQUFXLENBQUMsQ0FBQztJQUM1QyxDQUFDO0lBRUQsc0ZBQXNGO0lBQ3RGLDZCQUE2QjtJQUM3QixJQUFJLGNBQWM7UUFDaEIsTUFBTSxJQUFJLEdBQUcsSUFBSSxDQUFDLFVBQVUsQ0FBQztRQUM3QixPQUFPLENBQUEsSUFBSSxhQUFKLElBQUksdUJBQUosSUFBSSxDQUFFLElBQUksTUFBSyxTQUFTLENBQUMsQ0FBQyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsSUFBSSxhQUFKLElBQUksdUJBQUosSUFBSSxDQUFFLE9BQU8sQ0FBQztJQUM5RCxDQUFDO0lBRUQscUZBQXFGO0lBQ3JGLElBQUksc0JBQXNCO1FBQ3hCLE1BQU0sSUFBSSxHQUFHLElBQUksQ0FBQyxVQUFVLENBQUM7UUFDN0IsT0FBTyxDQUFBLElBQUksYUFBSixJQUFJLHVCQUFKLElBQUksQ0FBRSxJQUFJLE1BQUssU0FBUyxDQUFDLENBQUMsQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQyxTQUFTLENBQUM7SUFDM0QsQ0FBQztJQUVELElBQUksZ0JBQWdCO1FBQ2xCLE9BQU8sSUFBSSxDQUFDLFdBQVcsQ0FBQyx1QkFBdUIsS0FBSyxTQUFTO1lBQzNELElBQUksQ0FBQyxnQkFBZ0IsS0FBSyxJQUFJLENBQUMsYUFBYSxDQUFDO0lBQ2pELENBQUM7SUFFRDs7Ozs7OztPQU9HO0lBQ0gsMkJBQTJCLENBQUMsU0FBaUI7UUFDM0MsTUFBTSxLQUFLLEdBQUcsSUFBSSxDQUFDLFdBQVcsQ0FBQztRQUMvQixLQUFLLElBQUksQ0FBQyxHQUFHLFNBQVMsRUFBRSxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUMsRUFBRSxFQUFFLENBQUM7WUFDcEMsTUFBTSxJQUFJLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDO1lBQ3RCLElBQUksQ0FBQSxJQUFJLGFBQUosSUFBSSx1QkFBSixJQUFJLENBQUUsSUFBSSxNQUFLLFNBQVM7Z0JBQUUsT0FBTyxJQUFJLENBQUMsS0FBSyxHQUFHLENBQUMsQ0FBQztRQUN0RCxDQUFDO1FBQ0QsT0FBTyxDQUFDLENBQUM7SUFDWCxDQUFDO0lBRUQsc0ZBQXNGO0lBQ3RGLHlGQUF5RjtJQUN6RixrRUFBa0U7SUFDbEUsMkJBQTJCLENBQUMsZUFBdUI7UUFDakQsSUFBSSxlQUFlLElBQUksQ0FBQztZQUFFLE9BQU8sQ0FBQyxDQUFDO1FBQ25DLHNGQUFzRjtRQUN0Riw2Q0FBNkM7UUFDN0MsSUFBSSxlQUFlLEdBQUcsSUFBSSxDQUFDLFdBQVcsQ0FBQyxRQUFRLENBQUMsT0FBTyxDQUFDLE1BQU07WUFBRSxPQUFPLElBQUksQ0FBQyxhQUFhLENBQUM7UUFDMUYsT0FBTyxJQUFJLENBQUMsV0FBVyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxJQUFJLEtBQUssU0FBUyxJQUFJLENBQUMsQ0FBQyxLQUFLLEtBQUssZUFBZSxHQUFHLENBQUMsQ0FBQyxDQUFDO0lBQ2xHLENBQUM7SUFFRCxrQkFBa0IsQ0FBQyxPQUFnQztRQUNqRCxNQUFNLEtBQUssR0FBRyxJQUFJLENBQUMsV0FBVyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxJQUFJLEtBQUssU0FBUyxJQUFJLENBQUMsQ0FBQyxLQUFLLENBQUMsRUFBRSxLQUFLLE9BQU8sQ0FBQyxFQUFFLENBQUMsQ0FBQztRQUNqRyxPQUFPLElBQUksQ0FBQyxZQUFZLENBQUMsS0FBSyxDQUFDLENBQUM7SUFDbEMsQ0FBQztJQUVELDZFQUE2RTtJQUM3RSxJQUFJLGNBQWM7UUFDaEIsSUFBSSxJQUFJLENBQUMsYUFBYSxJQUFJLENBQUM7WUFBRSxPQUFPLEVBQUUsQ0FBQztRQUV2QyxNQUFNLFdBQVcsR0FBRyxJQUFJLEdBQUcsRUFBVSxDQUFDO1FBQ3RDLE1BQU0sT0FBTyxHQUFxQixFQUFFLENBQUM7UUFDckMsS0FBSyxNQUFNLE9BQU8sSUFBSSxJQUFJLENBQUMsV0FBVyxDQUFDLHVCQUF1QixFQUFFLENBQUM7WUFDL0QsSUFBSSxXQUFXLENBQUMsR0FBRyxDQUFDLE9BQU8sQ0FBQyxZQUFZLENBQUM7Z0JBQUUsU0FBUztZQUNwRCxXQUFXLENBQUMsR0FBRyxDQUFDLE9BQU8sQ0FBQyxZQUFZLENBQUMsQ0FBQztZQUN0QyxnRkFBZ0Y7WUFDaEYsbUJBQW1CO1lBQ25CLE9BQU8sQ0FBQyxJQUFJLENBQUM7Z0JBQ1gsT0FBTztnQkFDUCxRQUFRLEVBQUUsSUFBSSxDQUFDLFlBQVksQ0FBQyxJQUFJLENBQUMsMkJBQTJCLENBQUMsT0FBTyxDQUFDLFlBQVksR0FBRyxDQUFDLENBQUMsQ0FBQzthQUN4RixDQUFDLENBQUM7UUFDTCxDQUFDO1FBQ0QsT0FBTyxPQUFPLENBQUM7SUFDakIsQ0FBQztJQUVELGtCQUFrQixDQUFDLE9BQXNDO1FBQ3ZELElBQUksQ0FBQyxlQUFlLEdBQUcsT0FBTyxDQUFDO0lBQ2pDLENBQUM7SUFFSyxjQUFjLENBQUMsU0FBaUI7O1lBQ3BDLHNGQUFzRjtZQUN0Rix3RkFBd0Y7WUFDeEYsb0VBQW9FO1lBQ3BFLElBQUksQ0FBQyxhQUFhLEdBQUcsU0FBUyxJQUFJLElBQUksQ0FBQyxhQUFhLENBQUMsQ0FBQyxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUMsU0FBUyxDQUFDO1lBQ3pFLElBQUksQ0FBQyxlQUFlLEdBQUcsU0FBUyxDQUFDO1lBQ2pDLHNGQUFzRjtZQUN0RixxRkFBcUY7WUFDckYsNENBQTRDO1lBQzVDLElBQUksQ0FBQyxJQUFJLENBQUMsUUFBUSxFQUFFLENBQUM7Z0JBQ25CLElBQUksQ0FBQyxRQUFRLEdBQUcsSUFBSSxDQUFDLFdBQVcsRUFBRSxDQUFDLE9BQU8sQ0FBQyxHQUFHLEVBQUUsR0FBRyxJQUFJLENBQUMsUUFBUSxHQUFHLFNBQVMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxDQUFDO1lBQ25GLENBQUM7WUFDRCxNQUFNLElBQUksQ0FBQyxRQUFRLENBQUM7UUFDdEIsQ0FBQztLQUFBO0lBRUssV0FBVyxDQUFDLE9BQWdDOztZQUNoRCxNQUFNLEtBQUssR0FBRyxJQUFJLENBQUMsV0FBVyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxJQUFJLEtBQUssU0FBUyxJQUFJLENBQUMsQ0FBQyxLQUFLLENBQUMsRUFBRSxLQUFLLE9BQU8sQ0FBQyxFQUFFLENBQUMsQ0FBQztZQUNqRyxJQUFJLEtBQUssS0FBSyxDQUFDLENBQUMsRUFBRSxDQUFDO2dCQUNqQixNQUFNLElBQUksQ0FBQyxjQUFjLENBQUMsS0FBSyxDQUFDLENBQUM7WUFDbkMsQ0FBQztRQUNILENBQUM7S0FBQTtJQUVELFVBQVUsQ0FBQyxPQUFpQjtRQUMxQixNQUFNLFVBQVUsR0FBRyxPQUFPLEtBQUssU0FBUyxDQUFDLENBQUMsQ0FBQyxPQUFPLENBQUMsQ0FBQyxDQUFDLENBQUMsSUFBSSxDQUFDLGFBQWEsQ0FBQztRQUN6RSxzQkFBc0IsQ0FBQyxJQUFJLENBQUMsV0FBVyxFQUFFLFVBQVUsQ0FBQyxDQUFDLENBQUMsV0FBVyxDQUFDLENBQUMsQ0FBQyxVQUFVLENBQUMsQ0FBQztRQUNoRixJQUFJLENBQUMsYUFBYSxHQUFHLFVBQVUsQ0FBQztRQUNoQyxJQUFJLENBQUMsaUJBQWlCLEVBQUUsQ0FBQztRQUN6QixJQUFJLFVBQVU7WUFBRSxJQUFJLENBQUMsZUFBZSxFQUFFLENBQUM7SUFDekMsQ0FBQztJQUVELE9BQU87UUFDTCwyRkFBMkY7UUFDM0YseUZBQXlGO1FBQ3pGLElBQUksQ0FBQyxhQUFhLEdBQUcsS0FBSyxDQUFDO1FBQzNCLElBQUksQ0FBQyxpQkFBaUIsRUFBRSxDQUFDO1FBQ3pCLElBQUksQ0FBQyxpQkFBaUIsRUFBRSxDQUFDO0lBQzNCLENBQUM7SUFFYSxXQUFXOztZQUN2Qix1RkFBdUY7WUFDdkYsc0ZBQXNGO1lBQ3RGLHFCQUFxQjtZQUNyQixPQUFPLElBQUksQ0FBQyxlQUFlLEtBQUssU0FBUyxFQUFFLENBQUM7Z0JBQzFDLE1BQU0sTUFBTSxHQUFHLElBQUksQ0FBQyxlQUFlLENBQUM7Z0JBQ3BDLE1BQU0sUUFBUSxHQUFHLElBQUksQ0FBQywyQkFBMkIsQ0FBQyxNQUFNLENBQUMsQ0FBQztnQkFDMUQsd0ZBQXdGO2dCQUN4Rix1RkFBdUY7Z0JBQ3ZGLCtFQUErRTtnQkFDL0UsSUFBSSxLQUFLLEdBQUcsS0FBSyxDQUFDO2dCQUNsQixJQUFJLENBQUM7b0JBQ0gsTUFBTSxJQUFJLENBQUMsV0FBVyxDQUFDLHdCQUF3QixDQUFDLFFBQVEsQ0FBQyxDQUFDO2dCQUM1RCxDQUFDO2dCQUFDLE9BQU8sS0FBSyxFQUFFLENBQUM7b0JBQ2YsT0FBTyxDQUFDLElBQUksQ0FBQyxnREFBZ0QsRUFBRSxRQUFRLEVBQUUsU0FBUyxFQUFFLEtBQUssQ0FBQyxDQUFDO29CQUMzRixLQUFLLEdBQUcsSUFBSSxDQUFDO2dCQUNmLENBQUM7Z0JBQ0QsV0FBVyxDQUFDLEdBQUcsRUFBRTtvQkFDZixnRkFBZ0Y7b0JBQ2hGLG1GQUFtRjtvQkFDbkYsbUNBQW1DO29CQUNuQyxNQUFNLE1BQU0sR0FBRyxJQUFJLENBQUMsV0FBVyxDQUFDLHVCQUF1QixDQUFDO29CQUN4RCxNQUFNLE9BQU8sR0FBRyxLQUFLLElBQUksQ0FBQyxNQUFNLEtBQUssU0FBUyxJQUFJLE1BQU0sS0FBSyxRQUFRLENBQUMsQ0FBQztvQkFDdkUsSUFBSSxDQUFDLHNCQUFzQixHQUFHLE9BQU8sQ0FBQyxDQUFDLENBQUMsdUJBQXVCLENBQUMsQ0FBQyxDQUFDLElBQUksQ0FBQztvQkFDdkUsb0ZBQW9GO29CQUNwRiwrQ0FBK0M7b0JBQy9DLElBQUksT0FBTzt3QkFBRSxJQUFJLENBQUMsYUFBYSxHQUFHLFNBQVMsQ0FBQztvQkFDNUMsaUZBQWlGO29CQUNqRix5QkFBeUI7b0JBQ3pCLElBQUksSUFBSSxDQUFDLGVBQWUsS0FBSyxNQUFNO3dCQUFFLElBQUksQ0FBQyxlQUFlLEdBQUcsU0FBUyxDQUFDO2dCQUN4RSxDQUFDLENBQUMsQ0FBQztZQUNMLENBQUM7UUFDSCxDQUFDO0tBQUE7SUFFRCxxRkFBcUY7SUFDN0UsWUFBWSxDQUFDLFNBQWlCO1FBQ3BDLElBQUksSUFBSSxDQUFDLGFBQWEsSUFBSSxDQUFDO1lBQUUsT0FBTyxDQUFDLENBQUM7UUFDdEMsT0FBTyxJQUFJLENBQUMsR0FBRyxDQUFDLENBQUMsRUFBRSxJQUFJLENBQUMsR0FBRyxDQUFDLEdBQUcsRUFBRSxHQUFHLEdBQUcsQ0FBQyxTQUFTLEdBQUcsSUFBSSxDQUFDLGFBQWEsQ0FBQyxDQUFDLENBQUMsQ0FBQztJQUM1RSxDQUFDO0lBRU8sZUFBZTtRQUNyQixJQUFJLENBQUMsWUFBWSxHQUFHLFVBQVUsQ0FBQyxHQUFHLEVBQUUsQ0FBQyxLQUFLLElBQUksQ0FBQyxPQUFPLEVBQUUsRUFBRSxlQUFlLENBQUMsQ0FBQztJQUM3RSxDQUFDO0lBRU8saUJBQWlCO1FBQ3ZCLElBQUksSUFBSSxDQUFDLFlBQVksS0FBSyxTQUFTLEVBQUUsQ0FBQztZQUNwQyxZQUFZLENBQUMsSUFBSSxDQUFDLFlBQVksQ0FBQyxDQUFDO1lBQ2hDLElBQUksQ0FBQyxZQUFZLEdBQUcsU0FBUyxDQUFDO1FBQ2hDLENBQUM7SUFDSCxDQUFDO0lBRUQseUZBQXlGO0lBQ3pGLHlDQUF5QztJQUMzQixPQUFPOztZQUNuQixJQUFJLENBQUMsWUFBWSxHQUFHLFNBQVMsQ0FBQztZQUM5QixJQUFJLENBQUMsSUFBSSxDQUFDLGFBQWE7Z0JBQUUsT0FBTztZQUNoQyxJQUFJLElBQUksQ0FBQyxnQkFBZ0IsSUFBSSxJQUFJLENBQUMsYUFBYSxFQUFFLENBQUM7Z0JBQ2hELElBQUksQ0FBQyxVQUFVLENBQUMsS0FBSyxDQUFDLENBQUM7Z0JBQ3ZCLE9BQU87WUFDVCxDQUFDO1lBRUQsTUFBTSxJQUFJLENBQUMsY0FBYyxDQUFDLElBQUksQ0FBQyxnQkFBZ0IsR0FBRyxDQUFDLENBQUMsQ0FBQztZQUVyRCxvRkFBb0Y7WUFDcEYsSUFBSSxDQUFDLElBQUksQ0FBQyxhQUFhO2dCQUFFLE9BQU87WUFDaEMsOEVBQThFO1lBQzlFLElBQUksSUFBSSxDQUFDLHNCQUFzQixFQUFFLENBQUM7Z0JBQ2hDLElBQUksQ0FBQyxVQUFVLENBQUMsS0FBSyxDQUFDLENBQUM7Z0JBQ3ZCLE9BQU87WUFDVCxDQUFDO1lBQ0QsSUFBSSxDQUFDLGVBQWUsRUFBRSxDQUFDO1FBQ3pCLENBQUM7S0FBQTtDQUNGIn0=